"use client";

import React, { useState, useEffect } from "react";
import {
    Loader2, RefreshCw, Play, Save, ChevronDown, ChevronUp,
    Search, CheckCircle, AlertCircle, Undo2, Sparkles, ImageIcon, Edit3
} from "lucide-react";
import type { PluginMeta } from "@/lib/processors/base";
import type { Group } from "@/types/group.interface";
import type { Segment } from "@/types/segment.interface";
import { ImageLightbox } from "./ImageLightbox";

interface AutoSegmentReviewPanelProps {
    group: Group;
    plugin: PluginMeta;
    /** 原始用户输入（用于 Gemini 重试时提供上下文） */
    originalInputs: Record<string, string | number | string[]>;
    imagenPreviewUrls: string[];
    isGenerating?: boolean;
    onVideoStarted: () => void;
    onReset: () => void;
}

interface EditableSegment extends Segment {
    _editing: boolean;
    _draftDesc: string;
    _draftPrompt: string;
    _draftNegativePrompt: string;
    _dirty: boolean;
}

export const AutoSegmentReviewPanel: React.FC<AutoSegmentReviewPanelProps> = ({
    group,
    plugin,
    originalInputs,
    imagenPreviewUrls,
    isGenerating,
    onVideoStarted,
    onReset,
}) => {
    const [segments, setSegments] = useState<EditableSegment[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [isStartingVideo, setIsStartingVideo] = useState(false);
    const [isSaving, setIsSaving] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [globalConstraints, setGlobalConstraints] = useState("");
    const [isEditingGlobal, setIsEditingGlobal] = useState(false);
    const [draftGlobal, setDraftGlobal] = useState("");
    const [isSavingGlobal, setIsSavingGlobal] = useState(false);
    const [showImages, setShowImages] = useState(true);
    const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);

    const loadSegments = async () => {
        setIsLoading(true);
        try {
            const res = await fetch(`/api/segments?groupId=${group.id}`);
            const data: Segment[] = await res.json();
            setSegments(data.map(s => ({
                ...s,
                _editing: false,
                _draftDesc: s.desc || "",
                _draftPrompt: s.prompt,
                _draftNegativePrompt: s.negativePrompt ?? "",
                _dirty: false,
            })));
        } catch (e) {
            setError("加载镜头失败");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadSegments();
        const gc = group.globalConstraints ?? "";
        setGlobalConstraints(gc);
        setDraftGlobal(gc);
    }, [group.id]);

    // Phase 55: Poll for segment updates while generating
    useEffect(() => {
        if (!isGenerating) return;
        const interval = setInterval(() => {
            loadSegments();
        }, 5000);
        return () => clearInterval(interval);
    }, [isGenerating, group.id]);

    /** Wait until the SSE pipeline stage reaches `target` or the timeout elapses */
    const _pollUntilStage = (target: string, timeoutMs: number): Promise<void> => {
        return new Promise((resolve, reject) => {
            const es = new EventSource('/api/events');
            const timer = setTimeout(() => { es.close(); reject(new Error('等待超时')); }, timeoutMs);
            es.onmessage = (e) => {
                try {
                    const data = JSON.parse(e.data);
                    if (data.pipelineStage === target) {
                        clearTimeout(timer);
                        es.close();
                        resolve();
                    } else if (data.pipelineStage === 'error') {
                        clearTimeout(timer);
                        es.close();
                        reject(new Error(data.error || '流水线出错'));
                    }
                } catch { }
            };
            es.onerror = () => { clearTimeout(timer); es.close(); reject(new Error('SSE 连接断开')); };
        });
    };

    const toggleEdit = (idx: number) => {
        setSegments(prev => prev.map((s, i) =>
            i === idx ? { ...s, _editing: !s._editing } : s
        ));
    };

    const updateDraft = (idx: number, field: '_draftPrompt' | '_draftNegativePrompt' | '_draftDesc', value: string) => {
        setSegments(prev => prev.map((s, i) =>
            i === idx ? { ...s, [field]: value, _dirty: true } : s
        ));
    };

    const saveSegment = async (idx: number) => {
        const seg = segments[idx];
        if (!seg._dirty) { toggleEdit(idx); return; }
        setIsSaving(seg.id);
        try {
            const res = await fetch(`/api/segments/${seg.id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    desc: seg._draftDesc,
                    prompt: seg._draftPrompt,
                    negativePrompt: seg._draftNegativePrompt
                }),
            });
            if (!res.ok) throw new Error("保存失败");
            setSegments(prev => prev.map((s, i) =>
                i === idx ? {
                    ...s,
                    desc: s._draftDesc,
                    prompt: s._draftPrompt,
                    negativePrompt: s._draftNegativePrompt,
                    _editing: false,
                    _dirty: false,
                } : s
            ));
        } catch (e: any) {
            setError(e.message);
        } finally {
            setIsSaving(null);
        }
    };

    const discardEdit = (idx: number) => {
        setSegments(prev => prev.map((s, i) =>
            i === idx ? { ...s, _draftDesc: s.desc || "", _draftPrompt: s.prompt, _draftNegativePrompt: s.negativePrompt ?? "", _editing: false, _dirty: false } : s
        ));
    };

    const saveGlobalConstraints = async () => {
        setIsSavingGlobal(true);
        try {
            const res = await fetch(`/api/groups/${group.id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ globalConstraints: draftGlobal }),
            });
            if (!res.ok) throw new Error("保存全局约束失败");
            setGlobalConstraints(draftGlobal);
            setIsEditingGlobal(false);
        } catch (e: any) {
            setError(e.message);
        } finally {
            setIsSavingGlobal(false);
        }
    };

    const handleRegeneratePrompts = async () => {
        setIsRegenerating(true);
        setError(null);
        try {
            const res = await fetch('/api/pipeline/regenerate-prompts', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    groupId: group.id,
                    provider: plugin.id,
                    inputs: originalInputs,
                }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error);
            // 等待状态变回 review 再刷新
            await _pollUntilStage('review', 60000);
            await loadSegments();
        } catch (e: any) {
            setError(e.message);
        } finally {
            setIsRegenerating(false);
        }
    };

    const handleStartVideo = async () => {
        // 检查是否有未保存的编辑
        const hasDirty = segments.some(s => s._dirty);
        if (hasDirty) {
            setError("有未保存的编辑，请先保存所有镜头再生成视频");
            return;
        }
        setIsStartingVideo(true);
        setError(null);
        try {
            const res = await fetch('/api/pipeline/generate-video', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ groupId: group.id }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error);
            onVideoStarted();
        } catch (e: any) {
            setError(e.message);
            setIsStartingVideo(false);
        }
    };

    const hasDirtySegments = segments.some(s => s._dirty);

    const panel = (
        <div className="space-y-4">
            {/* Header */}
            <div className="bg-white dark:bg-zinc-900/60 border border-zinc-200 dark:border-zinc-800">
                <div className="px-5 py-4 flex items-center justify-between border-b border-zinc-200 dark:border-zinc-800">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-green-500/10 rounded-lg">
                            <CheckCircle size={16} className="text-green-500" />
                        </div>
                        <div>
                            <h3 className="text-sm font-semibold text-zinc-800 dark:text-zinc-200">AI 已生成，请审查镜头</h3>
                            <p className="text-[10px] text-zinc-400 dark:text-zinc-500 mt-0.5">
                                {segments.length} 个镜头 · 可编辑后再生成视频
                            </p>
                        </div>
                    </div>
                    <button onClick={onReset} className="text-xs text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 flex items-center gap-1 transition-colors">
                        <Undo2 size={12} /> 重新填写
                    </button>
                </div>

                {/* Action buttons */}
                <div className="px-5 py-3 flex flex-wrap gap-2 items-center">
                    <button
                        onClick={handleRegeneratePrompts}
                        disabled={isRegenerating || isStartingVideo}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-400 hover:border-purple-400 dark:hover:border-purple-500 hover:text-purple-600 dark:hover:text-purple-400 transition-colors disabled:opacity-40"
                    >
                        {isRegenerating
                            ? <Loader2 size={12} className="animate-spin" />
                            : <Sparkles size={12} />
                        }
                        Gemini 重试生成
                    </button>

                    {/* Global Constraints Editor */}
                    <div className="flex-1 min-w-[300px] flex items-center gap-2 px-3 py-1.5 bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200 dark:border-zinc-800 ml-2">
                        <span className="text-[10px] font-bold text-zinc-400 dark:text-zinc-500 shrink-0">GLOBAL CONSTRAINTS:</span>
                        {isEditingGlobal ? (
                            <div className="flex-1 flex items-center gap-1.5">
                                <input
                                    type="text"
                                    value={draftGlobal}
                                    onChange={e => setDraftGlobal(e.target.value)}
                                    className="flex-1 bg-transparent border-none text-[11px] text-zinc-800 dark:text-zinc-200 outline-none p-0 focus:ring-0"
                                    placeholder="输入全局生成约束..."
                                    autoFocus
                                />
                                <button onClick={() => { setDraftGlobal(globalConstraints); setIsEditingGlobal(false); }} className="text-[10px] text-zinc-400 hover:text-zinc-600">取消</button>
                                <button onClick={saveGlobalConstraints} disabled={isSavingGlobal} className="text-[10px] text-blue-500 font-bold hover:text-blue-600 flex items-center gap-1">
                                    {isSavingGlobal && <Loader2 size={10} className="animate-spin" />} 保存
                                </button>
                            </div>
                        ) : (
                            <div className="flex-1 flex items-center justify-between gap-4">
                                <span className="text-[11px] text-zinc-600 dark:text-zinc-400 truncate max-w-[400px]" title={globalConstraints}>
                                    {globalConstraints || "无全局约束"}
                                </span>
                                <button onClick={() => setIsEditingGlobal(true)} className="text-zinc-400 hover:text-zinc-600">
                                    <Edit3 size={12} />
                                </button>
                            </div>
                        )}
                    </div>

                    <button
                        onClick={handleStartVideo}
                        disabled={isStartingVideo || isRegenerating || hasDirtySegments || isGenerating}
                        title={hasDirtySegments ? "有未保存的编辑，请先保存" : ""}
                        className="flex items-center gap-1.5 px-4 py-1.5 text-xs bg-purple-600 hover:bg-purple-500 disabled:bg-zinc-200 dark:disabled:bg-zinc-700 disabled:text-zinc-400 text-white transition-all font-medium ml-auto"
                    >
                        {isStartingVideo || isGenerating
                            ? <Loader2 size={12} className="animate-spin" />
                            : <Play size={12} />
                        }
                        {isGenerating ? "视频生成中..." : isStartingVideo ? "提交中..." : hasDirtySegments ? "请先保存编辑" : "开始生成视频"}
                    </button>
                </div>
            </div>

            {/* Error */}
            {error && (
                <div className="flex items-center gap-2 text-xs text-red-500 dark:text-red-400 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 px-3 py-2">
                    <AlertCircle size={12} />
                    {error}
                </div>
            )}

            {/* Imagen preview images */}
            {imagenPreviewUrls.length > 0 && (
                <div className="bg-white dark:bg-zinc-900/60 border border-zinc-200 dark:border-zinc-800">
                    <button
                        onClick={() => setShowImages(v => !v)}
                        className="w-full px-5 py-3 flex items-center justify-between text-xs font-medium text-zinc-600 dark:text-zinc-400 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors"
                    >
                        <span className="flex items-center gap-2"><ImageIcon size={12} /> Imagen 素材图 ({imagenPreviewUrls.length} 张)</span>
                        {showImages ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                    </button>
                    {showImages && (
                        <div className="px-5 pb-4 flex flex-wrap gap-3">
                            {imagenPreviewUrls.map((url, i) => (
                                <div key={i} className="relative w-48 h-48 group/img border border-zinc-200 dark:border-zinc-700 rounded overflow-hidden bg-zinc-100 dark:bg-zinc-800">
                                    <img src={url} alt={`素材图 ${i + 1}`}
                                        className="w-full h-full object-cover transition-transform duration-500 group-hover/img:scale-110"
                                    />
                                    <div
                                        className="absolute inset-0 bg-black/40 opacity-0 group-hover/img:opacity-100 transition-opacity flex items-center justify-center cursor-zoom-in"
                                        onClick={() => setLightboxSrc(url)}
                                    >
                                        <button className="flex items-center gap-1.5 px-3 py-1.5 bg-white/20 hover:bg-white/30 backdrop-blur-md rounded-full text-white text-[10px] font-bold border border-white/20 transition-all">
                                            <Search size={12} /> 查看大图 (Zoom)
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                </div>
            )}

            {/* Segments */}
            {isLoading ? (
                <div className="flex items-center justify-center py-12 text-zinc-400">
                    <Loader2 size={20} className="animate-spin mr-2" /> 加载镜头...
                </div>
            ) : (
                <div className="space-y-2">
                    {segments.map((seg, idx) => (
                        <div key={seg.id}
                            className={`bg-white dark:bg-zinc-900/60 border transition-colors ${seg._dirty ? 'border-amber-300 dark:border-amber-600' : 'border-zinc-200 dark:border-zinc-800'}`}
                        >
                            {/* Segment header */}
                            <div className="px-4 py-3 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] font-mono text-zinc-400 bg-zinc-100 dark:bg-zinc-800 px-2 py-0.5 rounded">
                                        镜头 {seg.order}
                                    </span>
                                    {seg._dirty && (
                                        <span className="text-[9px] text-amber-500 font-medium">未保存</span>
                                    )}
                                </div>
                                <div className="flex items-center gap-1.5">
                                    {seg._editing ? (
                                        <>
                                            <button onClick={() => discardEdit(idx)}
                                                className="text-[11px] text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 px-2 py-1 transition-colors">
                                                取消
                                            </button>
                                            <button onClick={() => saveSegment(idx)}
                                                disabled={isSaving === seg.id}
                                                className="flex items-center gap-1 text-[11px] text-blue-600 dark:text-blue-400 hover:text-blue-500 px-2 py-1 font-medium transition-colors disabled:opacity-50">
                                                {isSaving === seg.id ? <Loader2 size={10} className="animate-spin" /> : <Save size={10} />}
                                                保存
                                            </button>
                                        </>
                                    ) : (
                                        <div className="flex items-center gap-2">
                                            {/* Phase 55: Inline processing / download UI */}
                                            {seg.status === "processing" && (
                                                <span className="flex items-center gap-1 px-2 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 text-[10px] font-medium rounded animate-pulse">
                                                    <Loader2 size={10} className="animate-spin" /> 生成中...
                                                </span>
                                            )}
                                            {seg.status === "completed" && seg.videoUrl && (
                                                <a
                                                    href={`/api/segments/${seg.id}/download`}
                                                    download
                                                    className="flex items-center gap-1.5 px-3 py-1 bg-purple-100 hover:bg-purple-200 dark:bg-purple-500/20 dark:hover:bg-purple-500/30 text-purple-700 dark:text-purple-300 text-[11px] font-medium rounded transition-colors break-keep whitespace-nowrap"
                                                >
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" x2="12" y1="15" y2="3" /></svg>
                                                    下载视频
                                                </a>
                                            )}
                                            <button onClick={() => toggleEdit(idx)}
                                                disabled={isGenerating || seg.status === "processing" || seg.status === "completed"}
                                                className="flex items-center gap-1 text-[11px] text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 px-2 py-1 transition-colors disabled:opacity-30 disabled:hover:text-zinc-400">
                                                <Edit3 size={10} /> 编辑
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Segment content */}
                            <div className="px-4 pb-4 space-y-3">
                                <div>
                                    <p className="text-[10px] text-zinc-400 dark:text-zinc-500 mb-1 font-medium">CHINESE PROMPT (PREVIEW & EDIT)</p>
                                    {seg._editing ? (
                                        <textarea
                                            value={seg._draftDesc}
                                            onChange={e => updateDraft(idx, '_draftDesc', e.target.value)}
                                            rows={2}
                                            placeholder="中文提示词 (用于纠错预览)..."
                                            className="w-full bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 px-3 py-2 text-xs text-zinc-800 dark:text-zinc-200 outline-none focus:border-blue-500 transition-colors leading-relaxed resize-none"
                                        />
                                    ) : (
                                        <p className="text-xs text-zinc-700 dark:text-zinc-300 leading-relaxed font-medium">{seg.desc}</p>
                                    )}
                                </div>
                                <div>
                                    <p className="text-[10px] text-zinc-400 dark:text-zinc-500 mb-1 font-medium">ENGLISH PROMPT (VIDEO OUTPUT)</p>
                                    {seg._editing ? (
                                        <textarea
                                            value={seg._draftPrompt}
                                            onChange={e => updateDraft(idx, '_draftPrompt', e.target.value)}
                                            rows={3}
                                            className="w-full bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 px-3 py-2 text-xs text-zinc-800 dark:text-zinc-200 outline-none focus:border-blue-500 transition-colors font-mono resize-none"
                                        />
                                    ) : (
                                        <p className="text-xs text-zinc-700 dark:text-zinc-300 font-mono leading-relaxed">{seg.prompt}</p>
                                    )}
                                </div>
                                <div>
                                    <p className="text-[10px] text-zinc-400 dark:text-zinc-500 mb-1 font-medium">NEGATIVE PROMPT</p>
                                    {seg._editing ? (
                                        <textarea
                                            value={seg._draftNegativePrompt}
                                            onChange={e => updateDraft(idx, '_draftNegativePrompt', e.target.value)}
                                            rows={2}
                                            className="w-full bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 px-3 py-2 text-xs text-zinc-800 dark:text-zinc-200 outline-none focus:border-blue-500 transition-colors font-mono resize-none"
                                        />
                                    ) : (
                                        <p className="text-xs text-zinc-500 dark:text-zinc-500 font-mono leading-relaxed">{seg.negativePrompt || "—"}</p>
                                    )}
                                </div>
                                {seg.extensionInstruction && (
                                    <div>
                                        <p className="text-[10px] text-zinc-400 dark:text-zinc-500 mb-1 font-medium">EXTENSION</p>
                                        <p className="text-xs text-zinc-500 dark:text-zinc-500 font-mono leading-relaxed italic">{seg.extensionInstruction}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    return (
        <>
            {panel}
            {lightboxSrc && (
                <ImageLightbox src={lightboxSrc!} onClose={() => setLightboxSrc(null)} />
            )}
        </>
    );
};
